import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { Star, ArrowRight, SlidersHorizontal } from "lucide-react";
import { fetchProducts } from "../lib/api";
import { useCart } from "../contexts/CartContext";
import { toast } from "sonner";

const CollectionsPage = () => {
  const { slug } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("featured");
  const { addItem } = useCart();

  useEffect(() => {
    fetchProducts()
      .then((data) => setProducts(data))
      .catch(() => toast.error("Could not load products"))
      .finally(() => setLoading(false));
  }, []);

  const title = slug
    ? slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "All Products";

  const sorted = useMemo(() => {
    const arr = [...products];
    if (sort === "price-asc") arr.sort((a, b) => a.price - b.price);
    if (sort === "price-desc") arr.sort((a, b) => b.price - a.price);
    if (sort === "rating") arr.sort((a, b) => b.rating - a.rating);
    return arr;
  }, [products, sort]);

  return (
    <main className="bg-[#FFF2EC]">
      <div className="max-w-7xl mx-auto px-4 pt-10 pb-16 md:pt-14 md:pb-24">
        <div className="mb-8 md:mb-12">
          <div className="text-[11px] uppercase tracking-[0.35em] text-[#DD4D2A] font-semibold mb-3">
            Collection
          </div>
          <h1 className="font-serif-display text-4xl md:text-6xl font-semibold text-[#1A1410]">
            {title}
          </h1>
          <p className="mt-3 text-[#1A1410]/65 max-w-xl">
            Explore premium rebounders engineered for better cardio, joint-friendly
            bounce and lasting results.
          </p>
        </div>

        <div className="flex items-center justify-between mb-6">
          <span className="text-sm text-[#1A1410]/60">
            {sorted.length} products
          </span>
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4 text-[#1A1410]/60" />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-white border border-[#1A1410]/15 rounded-full px-4 py-2 text-sm outline-none"
            >
              <option value="featured">Featured</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="rating">Top Rated</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="aspect-[3/4] bg-[#FFE4D6] rounded-2xl animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {sorted.map((p) => (
              <div
                key={p.id}
                className="hover-lift bg-[#FFF2EC] rounded-2xl overflow-hidden border border-[#1A1410]/10 flex flex-col group"
              >
                <Link
                  to={`/products/${p.slug}`}
                  className="relative block aspect-[4/5] overflow-hidden bg-[#FFE4D6]"
                >
                  <img
                    src={p.images[0]}
                    alt={p.name}
                    className="product-img absolute inset-0 w-full h-full object-cover"
                  />
                  {p.badge && (
                    <div className="absolute top-3 left-3 bg-[#1A1410] text-[#FFF2EC] text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full">
                      {p.badge}
                    </div>
                  )}
                </Link>
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-1.5 mb-2">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className="w-3.5 h-3.5 fill-[#DD4D2A] text-[#DD4D2A]"
                        />
                      ))}
                    </div>
                    <span className="text-xs text-[#1A1410]/60">
                      ({p.rating})
                    </span>
                  </div>
                  <h3 className="font-serif-display text-lg font-semibold text-[#1A1410] line-clamp-2">
                    {p.name}
                  </h3>
                  <div className="mt-3 flex items-baseline gap-2">
                    <span className="font-semibold text-lg">
                      ${p.price}.00
                    </span>
                    <span className="text-sm text-[#1A1410]/45 line-through">
                      ${p.compare_at}.00
                    </span>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <button
                      onClick={() => {
                        addItem({
                          id: p.id,
                          name: p.name,
                          price: p.price,
                          compareAt: p.compare_at,
                          images: p.images,
                        });
                        toast.success("Added to cart");
                      }}
                      className="btn-coral flex-1 py-2.5 rounded-full text-[12px] uppercase"
                    >
                      Add to cart
                    </button>
                    <Link
                      to={`/products/${p.slug}`}
                      className="btn-outline-dark px-4 py-2.5 rounded-full text-[12px] uppercase inline-flex items-center gap-1"
                    >
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
};

export default CollectionsPage;
